import { useMemo } from 'react'
import ReactApexChart from 'react-apexcharts'
import { ApexOptions } from 'apexcharts'
import { baseChart, tightGrid } from '../ChartFrame'
import { LiveChartFrame } from './LiveChartFrame'
import { CardShell } from './CardShell'
import { HourlyResponse, Resource } from './types'
import { ChartKind } from './sections'
import { SERIES, ChartTheme, themed, useChartTheme } from './chartTheme'
import { tooltipHtml } from './tooltip'
import { UNIT, compact } from './format'
import { T, useFormat, useT } from './i18n'
import { dayTypeLabel } from './DailyConsumptionCard'

const skippedNote = (h: HourlyResponse, t: T) =>
  h.skipped_days > 0 ? (
    <div className='fs-8 text-muted'>
      {t('PROFILES_DASHBOARD.HOURLY.SKIPPED', { count: h.skipped_days })}
    </div>
  ) : null

export function HeatmapCard({ hourly }: { hourly: Resource<HourlyResponse> }) {
  const t = useT()
  return (
    <CardShell
      title={t('PROFILES_DASHBOARD.HEATMAP.TITLE')}
      subtitle={t('PROFILES_DASHBOARD.HEATMAP.SUBTITLE', { unit: UNIT })}
      state={hourly}
      empty={(h) => (h.days_used ? null : t('PROFILES_DASHBOARD.HOURLY.EMPTY'))}
      render={(h) => <Heatmap data={h} />}
    />
  )
}

/** Three colour bands from the data's own maximum (Low / Medium / High). */
export function heatmapRanges(max: number, theme: ChartTheme, t: T) {
  if (!max) return []
  return [
    { from: 0, to: max * 0.4, color: theme.heat.low, name: t('PROFILES_DASHBOARD.HEATMAP.LOW') },
    {
      from: max * 0.4 + 0.0001,
      to: max * 0.75,
      color: theme.heat.mid,
      name: t('PROFILES_DASHBOARD.HEATMAP.MEDIUM'),
    },
    {
      from: max * 0.75 + 0.0001,
      to: max + 1,
      color: theme.heat.high,
      name: t('PROFILES_DASHBOARD.HEATMAP.HIGH'),
    },
  ]
}

function Heatmap({ data }: { data: HourlyResponse }) {
  const theme = useChartTheme()
  const t = useT()
  const f = useFormat()
  const series = useMemo(
    () =>
      data.heatmap.map((row) => ({
        name: f.weekdayShort(row.weekday),
        data: row.hours.map((v, h) => ({ x: `${String(h).padStart(2, '0')}:00`, y: v as number })),
      })),
    [data.heatmap, f]
  )
  const max = useMemo(
    () =>
      Math.max(0, ...data.heatmap.flatMap((r) => r.hours.filter((v): v is number => v !== null))),
    [data.heatmap]
  )
  const options: ApexOptions = useMemo(
    () =>
      themed(
        {
          chart: { ...baseChart, type: 'heatmap', toolbar: { show: false } },
          grid: tightGrid,
          dataLabels: { enabled: false },
          colors: [theme.heat.mid],
          stroke: { width: 1, colors: [theme.surface] },
          plotOptions: {
            heatmap: {
              radius: 2,
              useFillColorAsStroke: false,
              colorScale: { ranges: heatmapRanges(max, theme, t) },
            },
          },
          legend: { show: true, position: 'top', floating: false },
          xaxis: { type: 'category', labels: { trim: true, hideOverlappingLabels: true } },
          tooltip: {
            custom: ({ seriesIndex, dataPointIndex }: any) => {
              const row = data.heatmap[seriesIndex]
              const value = row?.hours[dataPointIndex]
              if (!row) return ''
              const hour = String(dataPointIndex).padStart(2, '0')
              return tooltipHtml(theme, `${f.weekdayLong(row.weekday)} ${hour}:00–${hour}:59`, [
                {
                  label: t('PROFILES_DASHBOARD.HEATMAP.TIP_AVERAGE'),
                  value:
                    value === null || value === undefined
                      ? t('PROFILES_DASHBOARD.HEATMAP.NO_DATA')
                      : f.kwh(value),
                },
                { label: t('PROFILES_DASHBOARD.COMMON.DAYS'), value: f.number(row.days) },
              ])
            },
          },
        },
        theme
      ),
    [max, theme, data.heatmap, t, f]
  )
  return (
    <>
      <LiveChartFrame>
        <ReactApexChart
          key={`heat-${data.meta.site.id}-${data.meta.meter_type}-${data.meta.start}-${data.meta.end}-${theme.mode}-${f.locale}`}
          options={options}
          series={series}
          type='heatmap'
          width='100%'
          height={300}
        />
      </LiveChartFrame>
      {skippedNote(data, t)}
    </>
  )
}

export function ProfilesCard({
  hourly,
  chartType,
}: {
  hourly: Resource<HourlyResponse>
  chartType: ChartKind
}) {
  const t = useT()
  return (
    <CardShell
      title={t('PROFILES_DASHBOARD.PROFILES.TITLE')}
      subtitle={t('PROFILES_DASHBOARD.PROFILES.SUBTITLE', { unit: UNIT })}
      state={hourly}
      empty={(h) => (h.days_used ? null : t('PROFILES_DASHBOARD.HOURLY.EMPTY'))}
      render={(h) => <Profiles data={h} chartType={chartType} />}
    />
  )
}

function Profiles({ data, chartType }: { data: HourlyResponse; chartType: ChartKind }) {
  const theme = useChartTheme()
  const t = useT()
  const f = useFormat()
  const shown = useMemo(() => data.profiles.filter((p) => p.values), [data.profiles])
  const options: ApexOptions = useMemo(
    () =>
      themed(
        {
          chart: { ...baseChart, type: chartType, toolbar: { show: false } },
          grid: tightGrid,
          stroke: { curve: 'straight', width: chartType === 'bar' ? 0 : 2 },
          dataLabels: { enabled: false },
          xaxis: {
            categories: data.slot_labels,
            tickAmount: 12,
            labels: { rotate: -45, hideOverlappingLabels: true },
          },
          yaxis: {
            title: { text: t('PROFILES_DASHBOARD.PROFILES.Y_AXIS', { unit: UNIT }) },
            labels: { formatter: compact },
            min: 0,
          },
          colors: [SERIES.blue, SERIES.green, SERIES.red],
          legend: { position: 'top', floating: false },
          tooltip: {
            shared: true,
            intersect: false,
            custom: ({ dataPointIndex }: any) =>
              tooltipHtml(
                theme,
                t('PROFILES_DASHBOARD.PROFILES.TIP_TITLE', {
                  slot: data.slot_labels[dataPointIndex] ?? '',
                }),
                shown.map((p, i) => ({
                  label: t('PROFILES_DASHBOARD.PROFILES.SERIES', {
                    type: dayTypeLabel(t, p.key),
                    count: p.days,
                  }),
                  value: f.kwh((p.values as number[])[dataPointIndex], 1),
                  color: [SERIES.blue, SERIES.green, SERIES.red][i],
                }))
              ),
          },
        },
        theme
      ),
    [data.slot_labels, chartType, theme, shown, t, f]
  )
  const series = shown.map((p) => ({
    name: t('PROFILES_DASHBOARD.PROFILES.SERIES', { type: dayTypeLabel(t, p.key), count: p.days }),
    data: p.values as number[],
  }))
  return (
    <>
      <LiveChartFrame>
        <ReactApexChart
          key={`profiles-${data.meta.site.id}-${data.meta.meter_type}-${data.meta.start}-${data.meta.end}-${chartType}-${theme.mode}-${f.locale}`}
          options={options}
          series={series}
          type={chartType}
          width='100%'
          height={360}
        />
      </LiveChartFrame>
      {skippedNote(data, t)}
    </>
  )
}
